
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sigma, Pi, Brackets, Plus, Minus, Equal, Divide,
  Infinity, Asterisk, Square, CircleX, ArrowRight
} from "lucide-react";
import { insertTextAtCursor } from '@/utils/textInsertionUtils';
import { 
  TooltipProvider, 
  Tooltip, 
  TooltipTrigger, 
  TooltipContent 
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";

interface FormulaSelectorProps {
  onCharacterSelect: (character: string) => void;
}

const FormulaSelector: React.FC<FormulaSelectorProps> = ({ onCharacterSelect }) => {
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const isMobile = useIsMobile();
  
  // All categories combined in an array
  const allSymbolCategories = [
    // Basic mathematical operations
    {
      name: "Basic",
      symbols: [
        { symbol: '+', label: 'Plus', icon: <Plus size={18} /> },
        { symbol: '−', label: 'Minus', icon: <Minus size={18} /> },
        { symbol: '×', label: 'Multiply', icon: <Asterisk size={18} /> },
        { symbol: '÷', label: 'Divide', icon: <Divide size={18} /> },
        { symbol: '=', label: 'Equal', icon: <Equal size={18} /> },
        { symbol: '≠', label: 'Not Equal', icon: null },
        { symbol: '≈', label: 'Approximately Equal', icon: null },
        { symbol: '%', label: 'Percent', icon: null },
        { symbol: '±', label: 'Plus-Minus', icon: null },
        { symbol: '∞', label: 'Infinity', icon: <Infinity size={18} /> },
        { symbol: '()', label: 'Parentheses', icon: <Brackets size={18} /> },
        { symbol: '[]', label: 'Brackets', icon: null },
        { symbol: '{}', label: 'Braces', icon: null },
      ]
    },
    // Greek alphabet letters
    {
      name: "Greek Letters",
      symbols: [
        { symbol: 'α', label: 'Alpha', icon: null },
        { symbol: 'β', label: 'Beta', icon: null },
        { symbol: 'γ', label: 'Gamma', icon: null },
        { symbol: 'Γ', label: 'Gamma (uppercase)', icon: null },
        { symbol: 'δ', label: 'Delta', icon: null },
        { symbol: 'Δ', label: 'Delta (uppercase)', icon: null },
        { symbol: 'ε', label: 'Epsilon', icon: null },
        { symbol: 'ζ', label: 'Zeta', icon: null },
        { symbol: 'η', label: 'Eta', icon: null },
        { symbol: 'θ', label: 'Theta', icon: null },
        { symbol: 'Θ', label: 'Theta (uppercase)', icon: null },
        { symbol: 'ι', label: 'Iota', icon: null },
        { symbol: 'κ', label: 'Kappa', icon: null },
        { symbol: 'λ', label: 'Lambda', icon: null },
        { symbol: 'Λ', label: 'Lambda (uppercase)', icon: null },
        { symbol: 'μ', label: 'Mu', icon: null },
        { symbol: 'ν', label: 'Nu', icon: null },
        { symbol: 'ξ', label: 'Xi', icon: null },
        { symbol: 'Ξ', label: 'Xi (uppercase)', icon: null },
        { symbol: 'ο', label: 'Omicron', icon: null },
        { symbol: 'π', label: 'Pi', icon: <Pi size={18} /> },
        { symbol: 'Π', label: 'Pi (uppercase)', icon: null },
        { symbol: 'ρ', label: 'Rho', icon: null },
        { symbol: 'σ', label: 'Sigma', icon: null },
        { symbol: 'Σ', label: 'Sigma (uppercase)', icon: <Sigma size={18} /> },
        { symbol: 'τ', label: 'Tau', icon: null },
        { symbol: 'υ', label: 'Upsilon', icon: null },
        { symbol: 'φ', label: 'Phi', icon: null },
        { symbol: 'Φ', label: 'Phi (uppercase)', icon: null },
        { symbol: 'χ', label: 'Chi', icon: null },
        { symbol: 'ψ', label: 'Psi', icon: null },
        { symbol: 'Ψ', label: 'Psi (uppercase)', icon: null },
        { symbol: 'ω', label: 'Omega', icon: null },
        { symbol: 'Ω', label: 'Omega (uppercase)', icon: null },
      ]
    },
    // Algebra
    {
      name: "Algebra",
      symbols: [
        { symbol: '∑', label: 'Summation', icon: <Sigma size={18} /> },
        { symbol: '∏', label: 'Product', icon: <Pi size={18} /> },
        { symbol: '√', label: 'Square Root', icon: null },
        { symbol: '∛', label: 'Cube Root', icon: null },
        { symbol: '∜', label: 'Fourth Root', icon: null },
        { symbol: 'xⁿ', label: 'Superscript', icon: null },
        { symbol: 'xₙ', label: 'Subscript', icon: null },
        { symbol: '∧', label: 'Logical AND', icon: null },
        { symbol: '∨', label: 'Logical OR', icon: null },
        { symbol: '¬', label: 'Logical NOT', icon: null },
        { symbol: '⊻', label: 'Logical XOR', icon: null },
        { symbol: '≤', label: 'Less Than or Equal', icon: null },
        { symbol: '≥', label: 'Greater Than or Equal', icon: null },
        { symbol: '∝', label: 'Proportional To', icon: null },
        { symbol: 'ℕ', label: 'Natural Numbers', icon: null },
        { symbol: 'ℤ', label: 'Integers', icon: null },
        { symbol: 'ℚ', label: 'Rational Numbers', icon: null },
        { symbol: 'ℝ', label: 'Real Numbers', icon: null },
        { symbol: 'ℂ', label: 'Complex Numbers', icon: null },
      ]
    },
    // Linear algebra
    {
      name: "Linear Algebra",
      symbols: [
        { symbol: '⟨', label: 'Left Angle Bracket', icon: null },
        { symbol: '⟩', label: 'Right Angle Bracket', icon: null },
        { symbol: '⊕', label: 'Direct Sum', icon: null },
        { symbol: '⊗', label: 'Tensor Product', icon: null },
        { symbol: '⊙', label: 'Hadamard Product', icon: null },
        { symbol: '⋅', label: 'Dot Product', icon: null },
        { symbol: '×', label: 'Cross Product', icon: <Asterisk size={18} /> },
        { symbol: '‖', label: 'Norm', icon: null },
        { symbol: '⊥', label: 'Perpendicular', icon: null },
        { symbol: '∥', label: 'Parallel', icon: null },
        { symbol: '∇', label: 'Nabla/Del', icon: null },
        { symbol: '∂', label: 'Partial Derivative', icon: null },
        { symbol: '⊤', label: 'Transpose', icon: null },
        { symbol: '⊣', label: 'Left Tack', icon: null },
        { symbol: '⊢', label: 'Right Tack', icon: null },
        { symbol: '⊦', label: 'Assertion', icon: null },
        { symbol: 'det', label: 'Determinant', icon: null },
        { symbol: 'tr', label: 'Trace', icon: null },
      ]
    },
    // Calculus & analysis
    {
      name: "Calculus",
      symbols: [
        { symbol: '∫', label: 'Integral', icon: null },
        { symbol: '∬', label: 'Double Integral', icon: null },
        { symbol: '∭', label: 'Triple Integral', icon: null },
        { symbol: '∮', label: 'Contour Integral', icon: null },
        { symbol: '∯', label: 'Surface Integral', icon: null },
        { symbol: '∰', label: 'Volume Integral', icon: null },
        { symbol: '∇', label: 'Nabla/Del', icon: null },
        { symbol: '∂', label: 'Partial Derivative', icon: null },
        { symbol: 'ℓ', label: 'Script small l', icon: null },
        { symbol: 'ℒ', label: 'Script capital L', icon: null },
        { symbol: 'ℱ', label: 'Script capital F', icon: null },
        { symbol: 'δ', label: 'Delta/Variation', icon: null },
        { symbol: '∆', label: 'Increment', icon: null },
        { symbol: '→', label: 'Right Arrow/Limit', icon: null },
        { symbol: '↦', label: 'Maps To', icon: null },
        { symbol: '↪', label: 'Hook Arrow', icon: null },
        { symbol: 'lim', label: 'Limit', icon: null },
        { symbol: '∞', label: 'Infinity', icon: <Infinity size={18} /> },
        { symbol: '…', label: 'Ellipsis', icon: null },
      ]
    },
    // Set theory
    {
      name: "Set Theory",
      symbols: [
        { symbol: '∈', label: 'Element of', icon: null },
        { symbol: '∉', label: 'Not Element of', icon: null },
        { symbol: '∋', label: 'Contains as Element', icon: null },
        { symbol: '∌', label: 'Does Not Contain', icon: null },
        { symbol: '⊂', label: 'Subset of', icon: null },
        { symbol: '⊃', label: 'Superset of', icon: null },
        { symbol: '⊆', label: 'Subset or Equal', icon: null },
        { symbol: '⊇', label: 'Superset or Equal', icon: null },
        { symbol: '⊄', label: 'Not Subset of', icon: null },
        { symbol: '⊅', label: 'Not Superset of', icon: null },
        { symbol: '∪', label: 'Union', icon: null },
        { symbol: '∩', label: 'Intersection', icon: null },
        { symbol: '∖', label: 'Set Difference', icon: null },
        { symbol: '∁', label: 'Complement', icon: null },
        { symbol: '∅', label: 'Empty Set', icon: null },
        { symbol: 'ℙ', label: 'Power Set', icon: null },
        { symbol: '|', label: 'Such That', icon: null },
        { symbol: '∀', label: 'For All', icon: null },
        { symbol: '∃', label: 'There Exists', icon: null },
        { symbol: '∄', label: 'There Does Not Exist', icon: null },
      ]
    },
    // Logic
    {
      name: "Logic",
      symbols: [
        { symbol: '∧', label: 'Logical AND', icon: null },
        { symbol: '∨', label: 'Logical OR', icon: null },
        { symbol: '¬', label: 'Logical NOT', icon: null },
        { symbol: '⊻', label: 'Logical XOR', icon: null },
        { symbol: '⊕', label: 'Exclusive OR', icon: null },
        { symbol: '→', label: 'Implication', icon: null },
        { symbol: '↔', label: 'Logical Equivalence', icon: null },
        { symbol: '⊢', label: 'Proves', icon: null },
        { symbol: '⊨', label: 'Models', icon: null },
        { symbol: '∀', label: 'For All', icon: null },
        { symbol: '∃', label: 'There Exists', icon: null },
        { symbol: '∄', label: 'There Does Not Exist', icon: null },
        { symbol: '□', label: 'Necessarily', icon: <Square size={18} /> },
        { symbol: '◇', label: 'Possibly', icon: null },
        { symbol: '⊤', label: 'Top/True', icon: null },
        { symbol: '⊥', label: 'Bottom/False', icon: null },
      ]
    },
    // Probability & statistics
    {
      name: "Statistics & Probability",
      symbols: [
        { symbol: 'P()', label: 'Probability', icon: null },
        { symbol: 'E()', label: 'Expected Value', icon: null },
        { symbol: 'Var()', label: 'Variance', icon: null },
        { symbol: 'σ', label: 'Standard Deviation', icon: null },
        { symbol: 'μ', label: 'Mean (Mu)', icon: null },
        { symbol: '∑', label: 'Summation', icon: <Sigma size={18} /> },
        { symbol: '∏', label: 'Product', icon: <Pi size={18} /> },
        { symbol: '∩', label: 'Intersection', icon: null },
        { symbol: '∪', label: 'Union', icon: null },
        { symbol: '|', label: 'Conditional', icon: null },
        { symbol: '⊥', label: 'Independence', icon: null },
        { symbol: '≈', label: 'Approximately Equal', icon: null },
        { symbol: '∝', label: 'Proportional To', icon: null },
        { symbol: '!', label: 'Factorial', icon: null },
        { symbol: '∫', label: 'Integral', icon: null },
        { symbol: '∂', label: 'Partial Derivative', icon: null },
        { symbol: '∞', label: 'Infinity', icon: <Infinity size={18} /> },
        { symbol: 'n!', label: 'Factorial', icon: null },
        { symbol: '(n k)', label: 'Binomial Coefficient', icon: null },
      ]
    },
    // Physics
    {
      name: "Physics",
      symbols: [
        { symbol: '∇', label: 'Nabla/Del', icon: null },
        { symbol: '∆', label: 'Delta/Change', icon: null },
        { symbol: '∂', label: 'Partial Derivative', icon: null },
        { symbol: '∫', label: 'Integral', icon: null },
        { symbol: '∮', label: 'Line Integral', icon: null },
        { symbol: 'γ', label: 'Gamma', icon: null },
        { symbol: 'λ', label: 'Wavelength', icon: null },
        { symbol: 'μ', label: 'Coefficient of Friction', icon: null },
        { symbol: 'ν', label: 'Frequency', icon: null },
        { symbol: 'ρ', label: 'Density', icon: null },
        { symbol: 'σ', label: 'Cross Section', icon: null },
        { symbol: 'τ', label: 'Time Constant', icon: null },
        { symbol: 'ω', label: 'Angular Velocity', icon: null },
        { symbol: 'Ω', label: 'Ohm', icon: null },
        { symbol: '℧', label: 'Siemens', icon: null },
        { symbol: '°', label: 'Degree', icon: null },
        { symbol: '⟨', label: 'Bracket (left)', icon: null },
        { symbol: '⟩', label: 'Bracket (right)', icon: null },
        { symbol: '⊗', label: 'Tensor Product', icon: null },
        { symbol: '⋅', label: 'Dot Product', icon: null },
        { symbol: '×', label: 'Cross Product', icon: <Asterisk size={18} /> },
      ]
    },
    // Geometry
    {
      name: "Geometry",
      symbols: [
        { symbol: '∠', label: 'Angle', icon: null },
        { symbol: '∡', label: 'Measured Angle', icon: null },
        { symbol: '∟', label: 'Right Angle', icon: null },
        { symbol: '∥', label: 'Parallel', icon: null },
        { symbol: '⊥', label: 'Perpendicular', icon: null },
        { symbol: '≅', label: 'Congruent To', icon: null },
        { symbol: '∼', label: 'Similar To', icon: null },
        { symbol: '△', label: 'Triangle', icon: null },
        { symbol: '□', label: 'Square', icon: <Square size={18} /> },
        { symbol: '○', label: 'Circle', icon: null },
        { symbol: '∘', label: 'Composition', icon: null },
        { symbol: '⊙', label: 'Circle Dot', icon: null },
        { symbol: '⊕', label: 'Circle Plus', icon: null },
        { symbol: '⊗', label: 'Circle Times', icon: null },
        { symbol: '∂', label: 'Partial Derivative', icon: null },
        { symbol: '∴', label: 'Therefore', icon: null },
        { symbol: '∵', label: 'Because', icon: null },
      ]
    },
    // Chemistry
    {
      name: "Chemistry",
      symbols: [
        { symbol: '→', label: 'Reaction Arrow', icon: null },
        { symbol: '⇌', label: 'Equilibrium', icon: null },
        { symbol: '↑', label: 'Gas Evolution', icon: null },
        { symbol: '↓', label: 'Precipitation', icon: null },
        { symbol: 'Δ', label: 'Heat', icon: null },
        { symbol: 'pH', label: 'pH', icon: null },
        { symbol: 'Ka', label: 'Acid Dissociation Constant', icon: null },
        { symbol: 'Kb', label: 'Base Dissociation Constant', icon: null },
        { symbol: 'Kw', label: 'Water Dissociation Constant', icon: null },
        { symbol: 'Kc', label: 'Equilibrium Constant', icon: null },
        { symbol: 'Kp', label: 'Pressure Equilibrium Constant', icon: null },
        { symbol: 'ΔH', label: 'Enthalpy Change', icon: null },
        { symbol: 'ΔG', label: 'Gibbs Free Energy Change', icon: null },
        { symbol: 'ΔS', label: 'Entropy Change', icon: null },
        { symbol: '°C', label: 'Degrees Celsius', icon: null },
        { symbol: '°F', label: 'Degrees Fahrenheit', icon: null },
        { symbol: 'K', label: 'Kelvin', icon: null },
        { symbol: 'mol', label: 'Mole', icon: null },
        { symbol: '°', label: 'Degree', icon: null },
        { symbol: 'μ', label: 'Micro', icon: null },
      ]
    }
  ];

  const handleNextCategory = () => {
    setCurrentCategoryIndex((prevIndex) => 
      prevIndex === allSymbolCategories.length - 1 ? 0 : prevIndex + 1);
  };

  const renderCharacterGrid = (characters: { symbol: string; label: string; icon: React.ReactNode | null }[]) => {
    // Force a dense multi-column layout on mobile by using a fixed 4-column grid.
    // Add an inline gridTemplateColumns for extra robustness on iOS WebViews.
    const containerClass = isMobile
      ? "grid grid-cols-4 gap-2 p-2 w-full"
      : "grid grid-cols-4 min-[380px]:grid-cols-5 min-[520px]:grid-cols-6 md:grid-cols-4 gap-2 p-2 w-full";
    const containerStyle = isMobile
      ? ({ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' } as React.CSSProperties)
      : undefined;

    return (
      <div className={containerClass} style={containerStyle}>
        {characters.map((char, index) => {
          const Btn = (
            <Button
              variant="outline"
              className="h-12 aspect-square w-full flex items-center justify-center text-lg p-0 min-w-0"
              onClick={() => onCharacterSelect(char.symbol)}
            >
              {char.icon || char.symbol}
            </Button>
          );

          return isMobile ? (
            <div key={index} className="min-w-0">{Btn}</div>
          ) : (
            <TooltipProvider key={index}>
              <Tooltip>
                <TooltipTrigger asChild>
                  {Btn}
                </TooltipTrigger>
                <TooltipContent>
                  <p>{char.label}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    );
  };

  const currentCategory = allSymbolCategories[currentCategoryIndex];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" title="Insert formula characters">
          <Sigma className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className={`${isMobile ? 'w-[95vw] max-w-[420px]' : 'w-[340px]'} p-0`} 
        sideOffset={5}
      >
        <div className="p-2 border-b flex justify-between items-center">
          <div className="font-medium">{currentCategory.name}</div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleNextCategory}
            className="flex items-center gap-1"
          >
            Next <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="max-h-[350px] overflow-y-auto">
          {renderCharacterGrid(currentCategory.symbols)}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default FormulaSelector;
